/* eslint-disable consistent-return */
import * as Yup from "yup";
import React, { useState } from "react";
import { FiMail } from "react-icons/fi";
import { Formik } from "formik";
import { MdClose, MdLocalPhone } from "react-icons/md";
import { toast } from "react-toastify";

import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import { cellPhoneNumberRegex } from "utils/regex";
import { useAuth } from "hooks/authHook";

import {
  ContentField,
  Field,
  FormContainer,
  FormRadio,
  FormRadioContent,
  ModalContainer,
  ModalHeader,
  ModalStyle,
  Paragraph,
  SelectTypeValidation,
  Title,
} from "./styles";

const ValidationContact = ({
  document,
  userId,
  show,
  handleCloseModal,
  tokenQuestions,
  setStepSideBar,
  setContactId,
}: any): JSX.Element => {
  const [step, setStep] = useState("validationOption");
  const [loading, setLoading] = useState(false);
  const [selectTypeValidation, setSelectTypeValidation] = useState("email");

  const { siteId, accessToken: access_token, customerCode } = useAuth();

  const sendToken = async (type, value): Promise<any> => {
    const headers = {
      access_token,
    };
    try {
      const bodyCreteContact = {
        contact: value,
        contactType: type,
        siteId,
        document,
        token: tokenQuestions,
      };

      const result = await api.post(
        `industrial/customer/v1/users/${userId}/contacts`,
        bodyCreteContact,
        {
          headers,
        }
      );
      const contactIdResponse = result.headers.location.split("/").pop();

      setContactId(contactIdResponse);

      const bodyToken = {
        tokenType: "MFA",
        url: "",
        contactId: contactIdResponse,
        document,
        customerCode,
      };

      const responseToken = await api.post(
        `industrial/customer/v1/users/${userId}/tokens`,
        bodyToken
      );

      toast.warn(`Enviamos um código de verificação para ${value}`);
      return responseToken;
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
      return err.response;
    }
  };

  const handlerSubmitEmail = async (data): Promise<void> => {
    setLoading(true);
    const response = await sendToken("EMAIL", data.email);
    const code = response?.data?.code;
    if (code === "422.010") {
      return setLoading(false);
    }
    setStepSideBar(2);
    setLoading(false);
  };

  const handlerSubmitPhone = async (data): Promise<void> => {
    setLoading(true);
    const value = data.phoneNumber.replace(/[^\d]/g, "");
    const response = await sendToken("PHONE", value);
    const code = response?.data?.code;
    if (code === "422.010") {
      return setLoading(false);
    }
    setStepSideBar(2);
    setLoading(false);
  };

  const resetStep = (): void => {
    setStep("validationOption");
  };

  const onHide = (): void => {
    handleCloseModal();
    resetStep();
  };

  const validatorSchemaEmail = (): any =>
    Yup.object().shape({
      email: Yup.string()
        .email("E-mail inválido")
        .required("É necessário um e-mail"),
    });

  const validatorSchemaPhone = (): any =>
    Yup.object().shape({
      phoneNumber: Yup.string()
        .required("É necessário digitar seu número de celular")
        .test("Telefone", "Número de telefone inválido", (value) => {
          if (value) {
            value = value.replace(/[^\d]/g, "");
            return !!value.match(cellPhoneNumberRegex);
          }
          return null;
        }),
    });

  return (
    <ModalStyle
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalContainer>
        <ModalHeader>
          <Title>Dados cadastrais</Title>
          {step !== "validationToken" && (
            <MdClose size={24} onClick={handleCloseModal} />
          )}
        </ModalHeader>
        <Paragraph>
          Valide pelo <b>menos um de seus contatos</b> pessoais
        </Paragraph>

        {step === "validationOption" && (
          <div>
            <SelectTypeValidation>
              <FormRadioContent>
                <FormRadio
                  custom
                  type="radio"
                  id="email"
                  name="type"
                  label="Email"
                  isSelect={selectTypeValidation === "email"}
                  onClick={() => setSelectTypeValidation("email")}
                />
                <FormRadio
                  custom
                  type="radio"
                  id="phone"
                  name="type"
                  label="Celular"
                  isSelect={selectTypeValidation === "phone"}
                  onClick={() => setSelectTypeValidation("phone")}
                />
              </FormRadioContent>
            </SelectTypeValidation>
            {selectTypeValidation === "email" && (
              <Formik
                initialValues={{
                  email: "",
                }}
                onSubmit={handlerSubmitEmail}
                validationSchema={validatorSchemaEmail}
                render={({
                  handleChange,
                  handleBlur,
                  values,
                  errors,
                  touched,
                }) => (
                  <FormContainer>
                    <span>Um código será enviado no email informado</span>
                    <ContentField>
                      <Field>
                        <TextField
                          LeadingIcon={FiMail}
                          propsLeadingIcon={{
                            size: 24,
                          }}
                          type="text"
                          name="email"
                          label="Email *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          errorCondition={!!errors.email && !!touched.email}
                          errorMessage={`${errors.email}`}
                          disabled={loading}
                        />
                      </Field>
                      <OutlinedButton
                        type="submit"
                        disabled={!values.email || !!errors.email || loading}
                        loading={loading}
                      >
                        Enviar token
                      </OutlinedButton>
                    </ContentField>
                  </FormContainer>
                )}
              />
            )}
            {selectTypeValidation === "phone" && (
              <Formik
                initialValues={{
                  phoneNumber: "",
                }}
                onSubmit={handlerSubmitPhone}
                validationSchema={validatorSchemaPhone}
                render={({
                  handleChange,
                  handleBlur,
                  values,
                  errors,
                  touched,
                }) => (
                  <FormContainer>
                    <span>Um código será enviado no celular informado</span>
                    <ContentField>
                      <Field>
                        <TextField
                          LeadingIcon={MdLocalPhone}
                          propsLeadingIcon={{
                            size: 24,
                          }}
                          name="phoneNumber"
                          mask="(99) 9 9999-9999"
                          label="Celular com DDD *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.phoneNumber}
                          errorCondition={
                            !!errors.phoneNumber && !!touched.phoneNumber
                          }
                          errorMessage={`${errors.phoneNumber}`}
                          disabled={loading}
                        />
                      </Field>
                      <OutlinedButton
                        type="submit"
                        disabled={
                          !values.phoneNumber || !!errors.phoneNumber || loading
                        }
                        loading={loading}
                      >
                        Enviar token
                      </OutlinedButton>
                    </ContentField>
                  </FormContainer>
                )}
              />
            )}
          </div>
        )}
      </ModalContainer>
    </ModalStyle>
  );
};

export default ValidationContact;
